import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Box,
  Stack,
  Switch,
  FormControlLabel,
  Typography,
  Collapse,
  Divider,
  Chip,
} from "@mui/material";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import PcpConfigFormFields, {
  buildPcpConfigInitialState,
  validatePcpConfig,
} from "./PcpConfigFormFields";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

const EmployeeFormDialog = ({
  open,
  onClose,
  onSave,
  employee = null,
  locations = [],
  jobPositions = [],
  employees = [],
  supportPcpOptions = [],
  existingPcpConfigs = [],
  loading = false,
}) => {
  const isEdit = Boolean(employee);
  const [form, setForm] = useState({});
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [error, setError] = useState("");

  // PCP config state — usado en crear (con primary location) y en editar (con location elegible)
  const [isPcp, setIsPcp] = useState(false);
  const [pcpConfig, setPcpConfig] = useState(buildPcpConfigInitialState());
  const [editPcpLocationId, setEditPcpLocationId] = useState("");

  const isAlreadyPcp = isEdit && existingPcpConfigs.length > 0;

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          employeeId: employee.employeeId,
          firstName: employee.firstName || "",
          lastName: employee.lastName || "",
          mail: employee.mail || "",
          businessEmail: employee.businessEmail || "",
          jobPositionId: employee.jobPositionId || "",
          managerId: employee.managerId || "",
          admission: employee.admission
            ? employee.admission.split("T")[0]
            : "",
          officePhone: employee.officePhone || "",
          extension: employee.extension || "",
          phone2: employee.phone2 || "",
          gender: employee.gender || "",
          dob: employee.dob ? employee.dob.split("T")[0] : "",
        });
        setSelectedLocationId("");
      } else {
        setForm({
          firstName: "",
          lastName: "",
          mail: "",
          businessEmail: "",
          jobPositionId: "",
          managerId: "",
          admission: new Date().toISOString().split("T")[0],
          officePhone: "",
          extension: "",
          phone2: "",
          gender: "",
          dob: "",
        });
        setSelectedLocationId("");
      }
      setIsPcp(false);
      setPcpConfig(buildPcpConfigInitialState());
      setEditPcpLocationId("");
      setError("");
    }
  }, [open, employee, isEdit]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePcpFieldChange = (field, value) => {
    setPcpConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.firstName?.trim()) return setError("First name is required");
    if (!form.lastName?.trim()) return setError("Last name is required");
    if (!form.jobPositionId) return setError("Job position is required");
    if (!form.admission) return setError("Admission date is required");
    if (!isEdit && !selectedLocationId)
      return setError("At least one location is required");

    if (isPcp && !isAlreadyPcp) {
      if (isEdit && !editPcpLocationId) {
        return setError("Select a clinical location for the PCP configuration.");
      }
      const pcpError = validatePcpConfig(pcpConfig);
      if (pcpError) return setError(pcpError);
    }

    const data = { ...form };
    if (!isEdit) {
      data.locations = JSON.stringify([
        { NUM_ID_LOCATION: Number(selectedLocationId), BIT_PRIMARY: 1 },
      ]);
    }
    Object.keys(data).forEach((k) => {
      if (data[k] === "") data[k] = isEdit ? null : undefined;
    });
    if (data.jobPositionId) data.jobPositionId = Number(data.jobPositionId);
    if (data.managerId) data.managerId = Number(data.managerId);

    // Pass an extra payload describing whether to also create a PCP config.
    let pcpPayload = null;
    if (isPcp && !isAlreadyPcp) {
      pcpPayload = {
        locationId: Number(isEdit ? editPcpLocationId : selectedLocationId),
        form: pcpConfig,
      };
    }

    onSave(data, pcpPayload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Employee" : "New Employee"}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="First Name *"
              value={form.firstName || ""}
              onChange={handleChange("firstName")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Last Name *"
              value={form.lastName || ""}
              onChange={handleChange("lastName")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Personal Email"
              value={form.mail || ""}
              onChange={handleChange("mail")}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Business Email"
              value={form.businessEmail || ""}
              onChange={handleChange("businessEmail")}
              fullWidth
              size="small"
              type="email"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Job Position *</InputLabel>
              <Select
                value={form.jobPositionId || ""}
                label="Job Position *"
                onChange={handleChange("jobPositionId")}
              >
                {jobPositions.map((jp) => (
                  <MenuItem key={jp.jobPositionId} value={jp.jobPositionId}>
                    {jp.jobPositionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Manager</InputLabel>
              <Select
                value={form.managerId || ""}
                label="Manager"
                onChange={handleChange("managerId")}
              >
                <MenuItem value="">None</MenuItem>
                {employees
                  .filter((e) => e.status === 1)
                  .map((e) => (
                    <MenuItem key={e.employeeId} value={e.employeeId}>
                      {e.firstName} {e.lastName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Admission Date *"
              value={form.admission || ""}
              onChange={handleChange("admission")}
              fullWidth
              size="small"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Date of Birth"
              value={form.dob || ""}
              onChange={handleChange("dob")}
              fullWidth
              size="small"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Office Phone"
              value={form.officePhone || ""}
              onChange={handleChange("officePhone")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Extension"
              value={form.extension || ""}
              onChange={handleChange("extension")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Phone 2"
              value={form.phone2 || ""}
              onChange={handleChange("phone2")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Gender</InputLabel>
              <Select
                value={form.gender || ""}
                label="Gender"
                onChange={handleChange("gender")}
              >
                <MenuItem value="">—</MenuItem>
                {GENDERS.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {!isEdit && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Primary Location *</InputLabel>
                <Select
                  value={selectedLocationId}
                  label="Primary Location *"
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                >
                  {locations
                    .filter((l) => l.status === 1)
                    .map((l) => (
                      <MenuItem key={l.locationId} value={l.locationId}>
                        {l.medicalCenter}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* PCP question — visible en crear y en editar */}
          <Grid size={{ xs: 12 }}>
            {isAlreadyPcp ? (
              // Empleado ya es PCP en una o más locations: resumen read-only.
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "rgba(99, 102, 241, 0.04)",
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      bgcolor: "rgba(99, 102, 241, 0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#4F46E5",
                      flexShrink: 0,
                    }}
                  >
                    <LocalHospitalOutlinedIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700}>
                      This employee is registered as a PCP
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      Configured in {existingPcpConfigs.length} location
                      {existingPcpConfigs.length !== 1 ? "s" : ""}. Manage capacity, accepted patient
                      types and additional locations from the Assignments module.
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                      {existingPcpConfigs.map((c) => (
                        <Chip
                          key={c.locationId}
                          label={c.medicalCenter}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: "#EDE9FE",
                            color: "#4F46E5",
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">Read-only</Typography>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              // Empleado nuevo o existente sin PCP config: toggle + formulario.
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isPcp ? "primary.main" : "divider",
                  bgcolor: isPcp ? "rgba(99, 102, 241, 0.04)" : "transparent",
                  transition: "all 0.15s",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: "rgba(99, 102, 241, 0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#4F46E5",
                      }}
                    >
                      <LocalHospitalOutlinedIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        Is this employee a PCP that receives patients?
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isEdit
                          ? "Toggle on to register the PCP configuration. You will pick the clinical location below."
                          : "Toggle on to register the PCP configuration in the primary location."}
                      </Typography>
                    </Box>
                  </Stack>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={isPcp}
                        onChange={(e) => setIsPcp(e.target.checked)}
                      />
                    }
                    label=""
                  />
                </Stack>

                <Collapse in={isPcp}>
                  <Divider sx={{ my: 2 }} />
                  {isEdit && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>PCP Clinical Location *</InputLabel>
                      <Select
                        value={editPcpLocationId}
                        label="PCP Clinical Location *"
                        onChange={(e) => setEditPcpLocationId(e.target.value)}
                      >
                        {locations
                          .filter((l) => l.status === 1 && l.clinical)
                          .map((l) => (
                            <MenuItem key={l.locationId} value={l.locationId}>
                              {l.medicalCenter}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                  <PcpConfigFormFields
                    value={pcpConfig}
                    onChange={handlePcpFieldChange}
                    supportPcpOptions={supportPcpOptions}
                  />
                </Collapse>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : isEdit ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeFormDialog;
